import type { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { ErrorSeverityMap } from '@kerberos-compliance/compliance-shared-ui';
import type { Observable } from 'rxjs';
import { catchError, throwError } from 'rxjs';
import { AmlAdminErrorHandlerService } from '../services/aml-admin-error-handler.service';
import { isWhitelistedEndpoint } from './utilities';

/**
 * An HTTP interceptor function to handle and respond to HTTP errors.
 *
 * Every failed request is reported to Sentry here and then re-thrown, so the caller can tell the user what went wrong: the caller
 * knows the context and shows the one toast through the error handler. Only the redirect a status code maps to (401 → login) is applied
 * here. Errors from translation file requests are excluded from this handling.
 *
 * @param request
 * @param next
 */
export const errorInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next,
): Observable<HttpEvent<unknown>> => {
  // Skip error handling completely for translation requests. This guard runs before anything is
  // injected: AmlAdminErrorHandlerService depends on the translation services, which issue the
  // translation request while being constructed — injecting it here would re-enter a root injector
  // that is still hydrating it and fail with NG0200.
  if (request.url && isWhitelistedEndpoint(request.url)) {
    return next(request);
  }

  // Resolved in the error path only, for the same reason.
  const injector = inject(Injector);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only handle non-translation errors
      if (error.url && !isWhitelistedEndpoint(error.url)) {
        injector.get(AmlAdminErrorHandlerService).reportHttpError(error, {
          severity: ErrorSeverityMap.High,
          component: 'errorInterceptor',
          action: 'request',
        });
      }
      return throwError(() => error);
    }),
  );
};
